import React, { useEffect, useState } from "react";
import {
  Text,
  useComputedColorScheme,
  useMantineTheme,
  LoadingOverlay,
} from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import { RichTextEditor, Link } from "@mantine/tiptap";
import { useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";

export interface TextSectionProps {
  title?: string;
  content?: string;
  isEditable?: boolean;
  placeholder?: string;
  onContentChange?: (content: string) => void;
  showToolbar?: boolean;
  variant?: "default" | "subtle";
}

export default function TextSection({
  title = "Text Content",
  content = "",
  isEditable = false,
  placeholder = "Start typing...",
  onContentChange,
  showToolbar = true,
  variant = "default",
}: TextSectionProps) {
  const theme = useMantineTheme();
  const isMobile = useMediaQuery(`(max-width: ${theme.breakpoints.xs})`);
  const colorScheme = useComputedColorScheme();
  const [isLoading, setIsLoading] = useState(false);

  const editor = useEditor({
    shouldRerenderOnTransaction: true,
    extensions: [
      StarterKit.configure({ link: false }),
      Link,
      //Superscript,
      //SubScript,
      //Highlight,
      //TextAlign.configure({ types: ["heading", "paragraph"] }),
    ],
    content: content,
    editable: isEditable,
    onUpdate: ({ editor }) => {
      if (onContentChange && isEditable) {
        onContentChange(editor.getHTML());
      }
    },
  });

  // Update editor content when prop changes
  useEffect(() => {
    if (editor && editor.getHTML() !== content) {
      editor.commands.setContent(content);
    }
  }, [content, editor]);

  // Update editor editable state when prop changes
  useEffect(() => {
    if (editor) {
      editor.setEditable(isEditable);
    }
  }, [isEditable, editor]);

  if (isLoading) {
    return (
      <div className="flex flex-col w-full h-full gap-4">
        <Text fz="md" c="dimmed">
          {title}
        </Text>
        <div className="w-full h-full rounded-lg border border-grey-800 overflow-hidden flex items-center justify-center">
          <LoadingOverlay
            visible={true}
            zIndex={400}
            overlayProps={{
              blur: "xl",
              color: colorScheme === "dark" ? "black" : "black",
              opacity: colorScheme === "dark" ? "70%" : "30%",
            }}
            loaderProps={{ color: "blue", type: "oval", size: "md" }}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col w-full h-full gap-4">
      <div className="w-full flex-1 overflow-hidden">
        <RichTextEditor 
          editor={editor} 
          variant={variant}
          style={{ height: "100%" }}
        >
          {showToolbar && isEditable && (
            <RichTextEditor.Toolbar sticky stickyOffset={0}>
              <RichTextEditor.ControlsGroup>
                <RichTextEditor.Bold />
                <RichTextEditor.Italic />
                <RichTextEditor.Underline />
                <RichTextEditor.Strikethrough />
                <RichTextEditor.ClearFormatting />
                <RichTextEditor.Highlight />
                <RichTextEditor.Code />
              </RichTextEditor.ControlsGroup>

              <RichTextEditor.ControlsGroup>
                <RichTextEditor.H1 />
                <RichTextEditor.H2 />
                <RichTextEditor.H3 />
                <RichTextEditor.H4 />
              </RichTextEditor.ControlsGroup>

              <RichTextEditor.ControlsGroup>
                <RichTextEditor.Blockquote />
                <RichTextEditor.Hr />
                <RichTextEditor.BulletList />
                <RichTextEditor.OrderedList />
                <RichTextEditor.Subscript />
                <RichTextEditor.Superscript />
              </RichTextEditor.ControlsGroup>

              <RichTextEditor.ControlsGroup>
                <RichTextEditor.Link />
                <RichTextEditor.Unlink />
              </RichTextEditor.ControlsGroup>

              <RichTextEditor.ControlsGroup>
                <RichTextEditor.AlignLeft />
                <RichTextEditor.AlignCenter />
                <RichTextEditor.AlignJustify />
                <RichTextEditor.AlignRight />
              </RichTextEditor.ControlsGroup>

              <RichTextEditor.ControlsGroup>
                <RichTextEditor.Undo />
                <RichTextEditor.Redo />
              </RichTextEditor.ControlsGroup>
            </RichTextEditor.Toolbar>
          )}

          <RichTextEditor.Content 
            style={{ 
              height: showToolbar && isEditable ? "calc(100% - 60px)" : "100%",
              padding: "16px",
              overflow: "auto"
            }}
          />
        </RichTextEditor>
      </div>
    </div>
  );
}
